import MainContent from '@/Layout/MainContent';
import { ColumnHeightOutlined, HistoryOutlined } from '@ant-design/icons';
import React, { useCallback } from 'react';
import Helmet from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import SearchForm from './searchForm';
import { Button, Select, Table, Typography } from 'antd';
import { formatDisplayDate, formatDisplayFullDate, TourGuideStatusType } from '@/config';
import { DEFAULT_PAGE_SIZE, GuideContractStatus, PAGE_SIZE_OPTION } from '@/constants';
import { GuideContractItem, GuideContractListingDto } from '@/__generated';
import { sumBy } from 'lodash';
import { useAppContext } from '@/contexts/AppContext';
import { ColumnsType, ExpandableConfig } from 'antd/lib/table/interface';
import { ApiSdk } from '@/httpclient';
import PdfPreviewModal from '@/components/PdfPreviewModal';
const { Paragraph } = Typography;

const PAGE_TITLE = 'GuideContractListingPage_PageTitle';
interface CustomGuideContractListingDto extends GuideContractListingDto {
  index: number;
}
const pageSize = 20;
export default function GuideContractListingPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [guideContracts, setGuideContracts] = React.useState<GuideContractListingDto[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [fileItem, setFileItem] = React.useState<GuideContractItem>();
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<string[]>([]);
  const [filters, setFilters] = React.useState<any>({
    pageSize,
    pageIndex: 1,
  });
  React.useEffect(() => {
    getData();
  }, [filters]);

  const onFilterChange = f => {
    setFilters(filter => ({ ...filter, ...f, pageIndex: 1 }));
  };
  const getData = () => {
    setLoading(true);
    ApiSdk.GuideContractService.getGuideContracts(filters)
      .then(res => {
        setGuideContracts(
          res.data?.map((d, i) => {
            return {
              ...d,
              index: i + 1,
              guideContractItems: d.guideContractItems?.map(file => {
                let parentData = { ...d, tourGuideId: (d?.tourGuideId || '') + file?.id };
                delete parentData['guideContractItems'];
                return {
                  ...parentData,
                  guideContractStatus: file.singed ? GuideContractStatus.Confirmed : GuideContractStatus.UnConfirmed,
                  ...file,
                };
              }),
            };
          }) || [],
        );
        setTotal(res.total || 0);
        setExpandedRowKeys(res.data?.map(x => x.tourGuideId || '') || []);
      })
      .catch(() => {
        setGuideContracts([]);
        setTotal(0);
        setExpandedRowKeys([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const expandable = React.useMemo(() => {
    const ex: ExpandableConfig<any> = {
      childrenColumnName: 'guideContractItems',
      expandedRowKeys,
      onExpandedRowsChange: ex => setExpandedRowKeys(ex as any),
    };
    return ex;
  }, [guideContracts, expandedRowKeys]);

  const onTableChange = useCallback(
    pagination => {
      const p = pagination?.current || 1;
      setFilters({
        ...filters,
        pageIndex: p,
        pageSize: pagination.pageSize,
      });
    },
    [filters],
  );
  const { auth } = useAppContext();

  const columns: ColumnsType<GuideContractListingDto> = React.useMemo(() => {
    return [
      {
        width: 60,
        fixed: 'left',
        title: t('No.'),
        dataIndex: 'index',
        shouldCellUpdate: () => false,
        render: v => <b>{v}</b>,
        align: 'center',
      },
      {
        width: 200,
        title: 'guideContractList_TourName',
        dataIndex: 'tourName',
        ellipsis: true,
        render: v => <b>{v}</b>,
      },
      {
        title: 'guideContractList_TourFrom',
        dataIndex: 'tourDateFrom',
        width: 130,
        ellipsis: true,
        render: v => formatDisplayDate(v),
      },
      {
        title: 'guideContractList_TourTo',
        dataIndex: 'tourDateTo',
        width: 130,
        ellipsis: true,
        render: v => formatDisplayDate(v),
      },
      {
        width: 150,
        title: 'guideContractList_TourID',
        dataIndex: 'tourId',
      },
      {
        width: 120,
        title: 'guideContractList_TourNo',
        dataIndex: 'tourNo',
      },
      {
        width: 150,
        title: 'guideContractList_GuideName',
        dataIndex: 'guideName',
        render: (v, record) => (
          <Paragraph style={{ marginBottom: 0 }} ellipsis={{ rows: record?.guideContractItems?.length || 2 }}>
            {v}
          </Paragraph>
        ),
      },

      {
        width: 150,
        title: 'guideContractList_GuideStatus',
        dataIndex: 'tourGuideStatus',
        render: v => <Trans>{TourGuideStatusType.__getValue(v)}</Trans>,
      },
      {
        width: 150,
        title: 'guideContractList_ContractStatus',
        dataIndex: 'guideContractStatus',
        render: v => (v ? GuideContractStatus.__getLabel(v) : undefined),
      },
      {
        width: 200,
        title: 'guideContractList_FileName',
        dataIndex: 'name',
        ellipsis: true,
        render: (v, record: any, x: any) => {
          return (
            <span title={v} style={{ cursor: 'pointer' }} onClick={() => setFileItem(record)}>
              {v}
            </span>
          );
        },
      },
      {
        title: 'guideContractList_ConfirmedDate',
        dataIndex: 'createdAt',
        width: 160,
        render: (v, record: any) => (record.singed ? formatDisplayFullDate(v) : undefined),
      },
    ].map(x => ({ ...x, title: t(x.title) })) as any;
  }, [auth]);

  const Width = React.useMemo(() => {
    return sumBy(columns, x => (x.width as any) || 0);
  }, [columns]);
  return (
    <MainContent title={t(PAGE_TITLE)} icon={<HistoryOutlined style={{ fontSize: 16 }} />}>
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body" style={{ marginBottom: 10 }}>
        <SearchForm onSearch={onFilterChange} />

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
          <div>{`${t('zTable.records')} ${total}`}</div>
          <div style={{ marginLeft: 20 }}>
            {t('zTable.pageSize')}
            <Select
              value={filters.pageSize}
              style={{ width: 90 }}
              onChange={v => {
                setFilters(q => ({ ...q, pageSize: parseInt((v as any) || filters.pageSize || DEFAULT_PAGE_SIZE) }));
              }}
            >
              {PAGE_SIZE_OPTION.map(page => (
                <Select.Option key={page} value={page}>
                  {page}
                </Select.Option>
              ))}
            </Select>
            <Button
              style={{ marginLeft: 10 }}
              icon={<ColumnHeightOutlined />}
              onClick={() => {
                setExpandedRowKeys(ex => (ex?.length ? [] : guideContracts?.map(x => x.tourGuideId || '') || []));
              }}
            />
          </div>
        </div>
        <Table
          scroll={{ x: Width }}
          loading={loading}
          columns={columns}
          dataSource={guideContracts}
          expandable={expandable}
          rowKey="tourGuideId"
          showSorterTooltip={false}
          pagination={{
            hideOnSinglePage: true,
            current: filters.pageIndex,
            pageSize: filters.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTION,
          }}
          onChange={onTableChange}
          bordered
          size="small"
          className="tour-evaluations-table"
        />
      </div>

      {fileItem ? <PdfPreviewModal fileId={fileItem.id || ''} fileName={fileItem.name || ''} onClose={() => setFileItem(undefined)} /> : null}
    </MainContent>
  );
}
